import React from "react";
import { Link } from "react-router-dom";
import { Quote, Star, CheckCircle2, ArrowRight, MessageCircle } from "lucide-react";
import { TESTIMONIALS, INSTITUTE } from "@/lib/content";
import useSEO from "@/hooks/useSEO";

function initials(name) {
  return name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

export default function Testimonials() {
  useSEO({
    title: "Student Testimonials | Kothari Classes, Jalgaon",
    description: "Read student testimonials from Kothari Classes, Jalgaon — real experiences from JEE, NEET, MH-CET and CBSE 10th students about our teaching and results.",
    canonical: "https://www.kothariclasses.in/testimonials",
  });
  const waHref = `https://wa.me/${INSTITUTE.whatsapp_number}?text=${encodeURIComponent(INSTITUTE.whatsapp_message)}`;
  return (
    <div data-testid="testimonials-page">
      {/* Header */}
      <section className="bg-gray-950 text-white py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-[0.04]" style={{backgroundImage:"radial-gradient(circle at 2px 2px, #fff 1px, transparent 1px)", backgroundSize:"32px 32px"}}/>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <span className="text-xs font-bold tracking-[0.22em] text-red-500 uppercase">Voices of Kothari</span>
            <h1 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1]">
              Words from students<br/>who studied here.
            </h1>
            <p className="mt-5 text-gray-400 text-lg max-w-xl">
              Real experiences from the batches of 2024 & 2025 — discipline, doubt-solving, mock tests, and results that followed.
            </p>
          </div>
        </div>
      </section>

      {/* Large first testimonial */}
      <section className="bg-white py-16 lg:py-20" data-testid="testimonials-featured-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative border border-gray-200 bg-gray-50 p-8 lg:p-14">
            <Quote className="w-16 h-16 text-red-600/20 absolute top-6 left-6" />
            <div className="relative grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-8 space-y-5">
                <div className="flex gap-1">
                  {[...Array(TESTIMONIALS[0].rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-red-600 text-red-600" />
                  ))}
                </div>
                <p className="font-display text-xl sm:text-2xl lg:text-3xl leading-snug tracking-tight text-gray-950">
                  "{TESTIMONIALS[0].quote}"
                </p>
                {TESTIMONIALS[0].highlights && (
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 pt-2">
                    {TESTIMONIALS[0].highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2.5 text-gray-700">
                        <CheckCircle2 className="w-4 h-4 text-red-600 mt-1 shrink-0" />
                        <span className="text-sm">{h}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="lg:col-span-4 lg:border-l lg:border-gray-200 lg:pl-8 flex lg:flex-col justify-between gap-4">
                <div className="flex items-center gap-4">
                  {TESTIMONIALS[0].photo ? (
                    <img
                      src={TESTIMONIALS[0].photo}
                      alt={TESTIMONIALS[0].name}
                      className="w-14 h-16 object-cover border border-red-200"
                      style={{ objectPosition: "50% 18%", filter: "brightness(1.05)" }}
                    />
                  ) : (
                    <div className="w-14 h-14 bg-red-600 text-white flex items-center justify-center font-display text-lg font-black">
                      {initials(TESTIMONIALS[0].name)}
                    </div>
                  )}
                  <div>
                    <div className="font-display font-bold text-gray-950">{TESTIMONIALS[0].name}</div>
                    <div className="text-xs uppercase tracking-widest text-gray-500">{TESTIMONIALS[0].role}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="eyebrow">Verified Student</div>
                  <div className="font-display text-3xl font-black text-gray-950">01</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Grid of testimonials */}
      <section className="bg-gray-50 py-16 lg:py-24" data-testid="testimonials-grid-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="eyebrow">Student Stories</span>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl font-black tracking-tight text-gray-950">
                More reasons to join.
              </h2>
            </div>
            <span className="text-xs uppercase tracking-widest text-gray-500 hidden sm:block">
              {TESTIMONIALS.length} reviews · avg. 5.0
            </span>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {TESTIMONIALS.slice(1).map((t, i) => (
              <article
                key={t.name}
                data-testid={`testimonial-card-${i}`}
                className="relative bg-white border border-gray-200 p-7 card-lift flex flex-col"
              >
                <Quote className="w-8 h-8 text-red-600/20 mb-4" />
                <div className="flex gap-1 mb-3">
                  {[...Array(t.rating)].map((_, ri) => (
                    <Star key={ri} className="w-3.5 h-3.5 fill-red-600 text-red-600" />
                  ))}
                </div>
                <p className="font-display text-lg font-semibold tracking-tight text-gray-950 leading-snug mb-3">
                  "{t.short}"
                </p>
                <p className="text-sm text-gray-600 leading-relaxed flex-1">{t.quote}</p>
                <div className="mt-6 pt-5 border-t border-gray-200 flex items-center gap-3">
                  {t.photo ? (
                    <img
                      src={t.photo}
                      alt={t.name}
                      className="w-11 h-14 object-cover border border-red-200"
                      style={{ objectPosition: "50% 18%", filter: "brightness(1.05)" }}
                    />
                  ) : (
                    <div className="w-11 h-11 border border-gray-300 bg-gray-50 flex items-center justify-center font-display text-sm font-black text-red-600">
                      {initials(t.name)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-gray-950 truncate">{t.name}</div>
                    <div className="text-[11px] uppercase tracking-widest text-gray-500 truncate">{t.role}</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-red-600 text-white py-16" data-testid="testimonials-cta">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center justify-between gap-6">
          <div>
            <span className="text-xs font-bold tracking-[0.22em] uppercase text-white/80">Write your own success story</span>
            <h2 className="font-display text-3xl lg:text-4xl font-black tracking-tight mt-2">Join the next batch.</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/admissions" className="bg-white text-red-600 font-semibold px-6 py-3.5 hover:bg-gray-100 transition-colors inline-flex items-center gap-2" data-testid="testimonials-enroll">
              Book Free Demo <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={waHref} target="_blank" rel="noopener noreferrer" className="border border-white text-white font-semibold px-6 py-3.5 hover:bg-white hover:text-red-600 transition-colors inline-flex items-center gap-2" data-testid="testimonials-whatsapp">
              <MessageCircle className="w-4 h-4" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
